// NotificationCard.jsx (compatibile React 16 / MUI v4)
import React from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Tooltip,
} from "@material-ui/core";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  DeleteOutlineOutlined,
  CheckCircleOutline, // stato: abilitata
  Block, // stato: disabilitata
} from "@material-ui/icons";
import { useIntl } from "react-intl";
import { common } from "../messages";
import NotificationCommonFields from "./NotificationCommonFields";
import { useBmapi } from "../utils/bmapi-context";

/**
 * Props attese:
 * - value: oggetto notifica esistente (id, event_type, title, active, show, remind_days, ...)
 * - onChange: (key, val) => void
 * - onSubmit: () => void
 * - onToggleExpand: () => void
 * - onDelete: () => Promise<void>
 * - consents: array
 * - imagesPath?: string | null
 * - notifConfig?: { events: Array<{ type: string, type_name: string, offset_type?: string }> }
 */
export default function NotificationCard({
  value,
  onChange,
  onSubmit,
  onToggleExpand,
  onDelete,
  consents,
  imagesPath,
  notifConfig,
}) {
  const intl = useIntl();
  const { startLoading, stopLoading } = useBmapi();

  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const isActive = !!value?.active;

  // Decodifica event_type → type_name usando notifConfig (fallback a title o event_type)
  const decodedTypeName = React.useMemo(() => {
    const et = value?.event_type;
    if (et && Array.isArray(notifConfig?.events)) {
      const found = notifConfig.events.find((e) => e?.type === et);
      if (found && (found.type_name || found.type)) {
        return found.type_name || found.type;
      }
    }
    return value?.title || et || "";
  }, [value?.event_type, value?.title, notifConfig]);

  // eventConfig da passare ai campi comuni (basato su value.event_type)
  const eventConfig = React.useMemo(() => {
    const et = value?.event_type;
    if (!et || !Array.isArray(notifConfig?.events)) return null;
    return notifConfig.events.find((e) => e?.type === et) || null;
  }, [notifConfig, value?.event_type]);

  // Titolo header: mostra type_name (o fallback) + eventualmente i giorni
  const headerTitle = React.useMemo(() => {
    if (!value) return "";
    const base = decodedTypeName;
    if (value.remind_days) {
      return base + " (" + value.remind_days + " gg)";
    }
    return base;
  }, [decodedTypeName, value]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  const openConfirm = () => setConfirmOpen(true);
  const closeConfirm = () => {
    if (!busy) setConfirmOpen(false);
  };

  const confirmDelete = async () => {
    try {
      setBusy(true);
      startLoading();
      await onDelete();
      setConfirmOpen(false);
    } finally {
      stopLoading();
      setBusy(false);
    }
  };

  return (
    <Box mb={2}>
      <Card>
        <CardHeader
          // Icona di stato a SINISTRA (visibile anche ad accordion chiuso)
          avatar={
            <Tooltip title={isActive ? "Abilitata" : "Disabilitata"}>
              <span>
                {isActive ? (
                  <CheckCircleOutline color="primary" />
                ) : (
                  <Block color="disabled" />
                )}
              </span>
            </Tooltip>
          }
          title={headerTitle}
          action={
            <Box display="flex" alignItems="center">
              {/* Cestino sempre visibile */}
              <Tooltip title={intl.formatMessage(common.delete) || "Elimina"}>
                <span>
                  <IconButton
                    onClick={openConfirm}
                    aria-label="delete"
                    disabled={busy}
                    edge="end"
                  >
                    <DeleteOutlineOutlined />
                  </IconButton>
                </span>
              </Tooltip>

              {/* Toggle espansione */}
              <IconButton
                onClick={onToggleExpand}
                aria-label="toggle"
                edge="end"
              >
                {value?.show ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            </Box>
          }
        />

        {value?.show && (
          <CardContent>
            <form onSubmit={handleSubmit}>
              <NotificationCommonFields
                value={value}
                onChange={onChange}
                consents={consents}
                imagesPath={imagesPath}
                eventConfig={eventConfig}
              />

              <Button
                variant="contained"
                fullWidth
                color="primary"
                type="submit"
                style={{ marginTop: 15 }}
              >
                {intl.formatMessage(common.update)}
              </Button>
            </form>
          </CardContent>
        )}
      </Card>

      {/* Dialog conferma cancellazione */}
      <Dialog open={confirmOpen} onClose={closeConfirm} fullWidth maxWidth="xs">
        <DialogTitle>
          {intl.formatMessage(common.delete) || "Elimina"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {intl.formatMessage(common.confirmDelete) ||
              "Sei sicuro di voler eliminare questa notifica?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} disabled={busy}>
            {intl.formatMessage(common.cancel) || "Annulla"}
          </Button>
          <Button
            onClick={confirmDelete}
            color="secondary"
            variant="contained"
            disabled={busy}
          >
            {intl.formatMessage(common.delete) || "Elimina"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
